import { useMarkets } from '@/state/markets';
import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useEmail } from '@/state/email';
import { addImpulse } from './prices';
import { sounds } from '@/audio/synth';

/**
 * Phone CTA action handlers. Each effectId maps to a function that mutates
 * game state. Random-event Phone messages reference these by id to make
 * Phone messages actually CONSEQUENTIAL — accepting/declining offers has
 * real cascading effects across pillars.
 */

type Handler = () => void;

export const PHONE_ACTION_HANDLERS: Record<string, Handler> = {
  // Saudi prince offers $50K to publicly endorse $BOOM. THE PLAYER tweets it (visible
  // in their own timeline), THEN cash is wired, THEN press follow-up reacts.
  'saudi-endorse-boom-accept': () => {
    // 1. The endorsement post — from YOUR account, immediately visible in your X-Clone feed
    useXClone.getState().composePost('$BOOM. patriotic. winning. sponsored, obviously.');
    // 2. Cash wires in 4 seconds (so the player sees the cause→effect chain)
    setTimeout(() => {
      useMarkets.getState().addCash(50_000);
      useLife.getState().bumpIrsHeat(8);
      usePower.getState().bumpRep('maga', 12);
      usePower.getState().bumpRep('left', -18);
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.04 });
      useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '+$50K endorsement wire cleared' });
      sounds.fill();
    }, 4_000);
    // 3. Press picks up the suspicious endorsement 14s later
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'odd: a "well-known retail trader" just publicly endorsed $BOOM for the first time ever. \$50K wire from a Riyadh-based account hit his fund 4 minutes later. unrelated, almost certainly.',
        likes: 8400, reposts: 2200,
      });
    }, 14_000);
  },
  'saudi-endorse-boom-decline': () => {
    usePower.getState().bumpNotoriety(-2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'passive', text: 'declined. saudi prince is not used to that.' });
  },

  // Lobbyist offer: tip-off → player tweets first → impulse → press dunks
  'lobbyist-pump-tari-accept': () => {
    useMarkets.getState().addCash(-5_000); // bribe the lobbyist
    // YOU tweet the pump bait — visible in your timeline
    useXClone.getState().composePost('$TARI is the trade of the week. trust me. no further comment.');
    addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.05 });
    useLife.getState().bumpSecHeat(15);
    usePower.getState().bumpRep('maga', 6);
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash', authorTribe: 'press',
        text: 'unusual options activity in $TARI 6 minutes before today\'s rally. one trader\'s tweet preceded the move by 14 seconds. SEC: "looking into it."',
        likes: 3200, reposts: 740,
      });
    }, 12_000);
    sounds.fill();
  },
  'lobbyist-pump-tari-decline': () => {
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'passive', text: 'declined. lobbyist will find other clients.' });
  },

  // Foreign intel — cash hits with explicit notification + delayed press blowback
  'intel-state-secrets-accept': () => {
    useLife.getState().bumpFbiHeat(20);
    useLife.getState().bumpSanity(-10);
    useMarkets.getState().addCash(80_000);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '+$80K wire from "[unknown source]"' });
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'EXCLUSIVE: anonymous American national signs deal with foreign intelligence. analysts call it "concerning." stock unaffected.',
        likes: 5400, reposts: 1100,
      });
    }, 12_000);
    sounds.notifyAlert();
  },
  'intel-state-secrets-decline': () => {
    useLife.getState().bumpSanity(2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'passive', text: 'declined. you sleep slightly better tonight.' });
  },

  // Senator offers a sponsorship — money wires + senator publicly thanks you
  'senator-sponsor-accept': () => {
    useMarkets.getState().addCash(20_000);
    useLife.getState().bumpIrsHeat(3);
    usePower.getState().bumpInfluence(8);
    usePower.getState().bumpRep('maga', 4);
    usePower.getState().bumpRep('left', -4);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '+$20K sponsorship wired' });
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'PatriotPete1776', authorTribe: 'maga',
        text: 'proud to call a "well-known retail trader" a partner of the campaign. WINNING.',
        likes: 6400, reposts: 1400,
      });
    }, 8_000);
    sounds.fill();
  },
  'senator-sponsor-decline': () => {
    usePower.getState().bumpRep('press', 5);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'passive', text: 'declined sponsorship. press will note your "principles."' });
  },

  // PR firm — money wires, then a "soft news" piece runs from the press tribe
  'pr-firm-launder-accept': () => {
    useMarkets.getState().addCash(-15_000);
    usePower.getState().bumpNotoriety(-12);
    Object.keys(usePower.getState().tribeRep).forEach((t) => {
      const cur = usePower.getState().tribeRep[t as keyof ReturnType<typeof usePower.getState>['tribeRep']];
      usePower.getState().bumpRep(t as never, -Math.sign(cur) * 5);
    });
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '-$15K PR retainer cleared' });
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'a "well-known retail trader" — recently the subject of much controversy — has been described in a new profile as "thoughtful" and "complicated." profile was reportedly placed by his PR firm. also called "thoughtful" and "complicated."',
        likes: 4400, reposts: 1100,
      });
    }, 10_000);
    sounds.fill();
  },

  // Mom — visible cash deduction + Mom thanks you in chat
  'mom-rent-accept': () => {
    useMarkets.getState().addCash(-200);
    useLife.getState().bumpRelationship('mom', 15);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'info', text: '-$200 to mom' });
    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'mom', fromNpc: 'mom',
        text: 'sweetie thank you. you saved me.',
      });
    }, 3_000);
  },
  'mom-rent-decline': () => {
    useLife.getState().bumpRelationship('mom', -25);
    useEmail.getState().receive({
      fromNpc: 'mom',
      fromAddress: 'mom@aol.com',
      subject: 'I really thought you would help.',
      body: "i raised you better than this. love mom",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'important', text: 'mom: I really thought you would help.' });
  },

  // Best friend — cash out, friend thanks, and a future "where\'s my money" beat lands
  'friend-loan-accept': () => {
    useMarkets.getState().addCash(-5_000);
    useLife.getState().bumpRelationship('best_friend', 20);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'info', text: '-$5,000 loaned to best friend' });
    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'best_friend', fromNpc: 'best_friend',
        text: 'bro you saved my ass. ill pay u back next month for sure.',
      });
    }, 3_000);
    // Five minutes later, friend "forgets"
    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'best_friend', fromNpc: 'best_friend',
        text: 'hey what loan',
      });
    }, 5 * 60_000);
  },
  'friend-loan-decline': () => {
    useLife.getState().bumpRelationship('best_friend', -30);
    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'best_friend', fromNpc: 'best_friend',
        text: 'cool bro. cool.',
      });
    }, 3_000);
  },

  // Off-shore lawyer — cash deducted, audit "disappears" via lawyer ack
  'offshore-clean-accept': () => {
    useMarkets.getState().addCash(-30_000);
    useLife.getState().bumpIrsHeat(-30);
    useLife.getState().bumpFbiHeat(8);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '-$30K cleared to lawyer' });
    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'lawyer', fromNpc: 'lawyer',
        text: 'audit notice "withdrawn." don\'t ask. don\'t tell. you owe me a steak.',
      });
    }, 4_000);
    sounds.fill();
  },

  // Crypto VC — cash in, YOU tweet the launch (visible in YOUR feed), VC also tweets
  'crypto-vc-accept': () => {
    useMarkets.getState().addCash(150_000);
    usePower.getState().bumpRep('crypto', 25);
    useLife.getState().bumpSecHeat(20);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '+$150K crypto VC raise cleared' });
    // YOU announce it (player\'s own tweet)
    useXClone.getState().composePost('announcing: a coin. mine. backed by a friend. utility: TBD. roadmap: also TBD. WAGMI.');
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WAGMI_VIP', authorTribe: 'crypto',
        text: 'i led the round. ive seen the deck. theres no deck. \$150M well spent. WAGMI.',
        likes: 11200, reposts: 2800,
      });
    }, 8_000);
  },

  // Insider tips — schedule a real impulse 60 seconds out, charge SEC heat
  'insider-tip-chip-accept': () => {
    useLife.getState().bumpSecHeat(20);
    setTimeout(() => {
      import('./prices').then((m) => m.addImpulse({
        ticker: 'CHIP', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.07,
      }));
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: '$CHIP popping. as promised.' });
    }, 60_000);
  },
  'insider-tip-tari-accept': () => {
    useLife.getState().bumpSecHeat(15);
    setTimeout(() => {
      import('./prices').then((m) => m.addImpulse({
        ticker: 'TARI', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.05,
      }));
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: '$TARI moving. timing was good.' });
    }, 60_000);
  },
  'insider-tip-rugs-short': () => {
    useLife.getState().bumpSecHeat(25);
    setTimeout(() => {
      import('./prices').then((m) => m.addImpulse({
        ticker: 'RUGS', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: -0.30,
      }));
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: '$RUGS crashed. you\'re welcome.' });
    }, 90_000);
  },

  // SEC settlement
  'sec-settle-accept': () => {
    useMarkets.getState().addCash(-50_000);
    useLife.getState().bumpSecHeat(-40);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'urgent', text: '-$50,000 SEC settlement' });
  },
  'sec-go-to-trial': () => {
    // Triggers the interactive court hearing mini-arc instead of random outcome
    import('./courtHearing').then((m) => m.startSecCourtHearing());
  },

  'rogan-accept': () => {
    import('./podcastAppearance').then((m) => m.startPodcastAppearance());
  },
  'rogan-decline': () => {
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined Rogan. Joe is not used to that.' });
  },

  'yacht-accept': () => {
    import('./yachtArc').then((m) => m.startYachtTrip());
  },
  'yacht-decline': () => {
    useLife.getState().bumpSanity(2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the yacht. you sleep slightly better tonight.' });
  },

  'rally-attend': () => {
    import('./rallyArc').then((m) => m.startRallyArc());
  },
  'rally-decline': () => {
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the rally. senator will not forget.' });
    usePower.getState().bumpRep('maga', -3);
  },

  'pump-accept': () => {
    import('./cryptoPumpArc').then((m) => m.startCryptoPumpArc());
  },
  'pump-decline': () => {
    useLife.getState().bumpSanity(3);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the pump. SEC will never know.' });
  },

  'run-accept': () => {
    import('./runForOfficeArc').then((m) => m.startRunForOfficeArc());
  },
  'run-decline': () => {
    useLife.getState().bumpSanity(2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined to run. democracy continues without you.' });
  },

  'gala-host': () => {
    import('./charityGalaArc').then((m) => m.startCharityGalaArc());
  },
  'gala-decline': () => {
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined to host. foundation sits dormant. tax accountant disappointed.' });
  },

  'tiktok-accept': () => {
    import('./tiktokArc').then((m) => m.startTikTokArc());
  },
  'tiktok-decline': () => {
    useLife.getState().bumpSanity(2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the algorithm. it will not forget.' });
  },

  'takeover-accept': () => {
    import('./hostileTakeoverArc').then((m) => m.startHostileTakeoverArc());
  },
  'takeover-decline': () => {
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the takeover. competitor breathes easier. for now.' });
  },

  'cult-found': () => {
    import('./cultArc').then((m) => m.startCultArc());
  },
  'cult-decline': () => {
    useLife.getState().bumpSanity(3);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined to start a cult.' });
  },

  'affair-accept': () => {
    import('./affairArc').then((m) => m.startAffairArc());
  },
  'affair-decline': () => {
    useLife.getState().bumpSanity(3);
    useLife.getState().bumpRelationship('wife', 4);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the match. moral fortitude unlocked.' });
  },

  'fed-day-trade': () => {
    import('./fedDecisionArc').then((m) => m.startFedDecisionArc());
  },
  'fed-day-skip': () => {
    useLife.getState().bumpSanity(2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'skipped Fed Day.' });
  },

  'earnings-host': () => {
    import('./earningsCallArc').then((m) => m.startEarningsCallArc());
  },
  'earnings-skip': () => {
    useLife.getState().bumpSanity(2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'CFO will read prepared remarks.' });
  },

  'taxes-file': () => {
    import('./taxSeasonArc').then((m) => m.startTaxSeasonArc());
  },
  'taxes-extension': () => {
    useLife.getState().bumpIrsHeat(8);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'extension filed. you have 6 months to procrastinate further.' });
  },

  'pardon-pursue': () => {
    import('./pardonArc').then((m) => m.startPardonArc());
  },
  'pardon-decline': () => {
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined to pursue a pardon. lawyer billed for the consult anyway.' });
    useMarkets.getState().addCash(-2_000);
  },

  'asset-activate': () => {
    import('./foreignAssetArc').then((m) => m.startForeignAssetArc());
  },
  'asset-decline': () => {
    useLife.getState().bumpFbiHeat(-2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: '[unknown]: "we will remember this."' });
  },

  'bloomberg-accept': () => {
    import('./bloombergInterviewArc').then((m) => m.startBloombergInterviewArc());
  },
  'bloomberg-decline': () => {
    usePower.getState().bumpRep('press', -8);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined Bloomberg. they will write the story anyway. without your quotes.' });
  },

  // ─── Wave 8 handlers ───
  'wellness-retreat-accept': () => {
    useMarkets.getState().addCash(-3_000);
    useLife.getState().bumpRelationship('wife', 12);
    useLife.getState().bumpSanity(8);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '-$3K wellness retreat. wife rep +12. sanity +8. you returned a slightly different person.' });
  },
  'wellness-retreat-decline': () => {
    useLife.getState().bumpRelationship('wife', -8);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the retreat. wife is, briefly, calling her therapist about you.' });
  },
  'influencer-pact-accept': () => {
    useMarkets.getState().addCash(-8_000);
    useXClone.getState().bumpFollowers(3_000);
    usePower.getState().bumpNotoriety(3);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '-$8K cross-promo. +3K followers. mostly bots.' });
  },
  'hedge-job-accept': () => {
    useMarkets.getState().addCash(220_000);
    useLife.getState().bumpSanity(15);
    useLife.getState().bumpRelationship('wife', 10);
    useLife.getState().bumpSecHeat(-15);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '+$220K signing bonus. sanity +15. wife approves. SEC heat -15. (your trading is, technically, not yours anymore.)' });
  },
  'hedge-job-decline': () => {
    usePower.getState().bumpNotoriety(2);
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'declined the hedge fund job. you are, structurally, still the protagonist.' });
  },
  'ghost-book-accept': () => {
    useMarkets.getState().addCash(15_000);
    usePower.getState().bumpNotoriety(8);
    usePower.getState().bumpRep('press', -2);
    useNotifications.getState().push({ surfaceId: 'wallet', severity: 'important', text: '+$15K book advance. notoriety +8. press rep -2 (book is, allegedly, fiction).' });
  },
};

export function invokePhoneAction(effectId: string): void {
  const handler = PHONE_ACTION_HANDLERS[effectId];
  if (!handler) {
    console.warn(`[phone-action] no handler for ${effectId}`);
    return;
  }
  handler();
}
